/* ============================================================================
 * SHARED MARKETING CONSTANTS (Wave 0 PR 0.5, 2026-04-25)
 *
 * Reusable building blocks for `*_COPY` exports. Each constant is the
 * single canonical form for a repeated piece of content. When a page
 * legitimately diverges (e.g. /partners CTA is "Apply as Partner"), the
 * divergence is left inline with a `// canonical-divergence:` comment.
 * ========================================================================== */

import { BRAND } from './brand';

/** Default primary CTA for any page that wants the canonical "Get started → /auth/register" pair. */
export const DEFAULT_PRIMARY_CTA = {
  label: BRAND.primaryCtaLabel,
  href: BRAND.primaryCtaHref,
} as const;

/** Build a UTM-tagged primary CTA preserving the canonical label + base path. */
export function utmCta(params: {
  readonly label?: string;
  readonly plan?: 'spark' | 'ignite' | 'inferno';
  readonly source: string;
  readonly medium?: string;
  readonly campaign?: string;
}): { readonly label: string; readonly href: string } {
  const qs = new URLSearchParams();
  if (params.plan) qs.set('plan', params.plan);
  qs.set('utm_source', params.source);
  if (params.medium) qs.set('utm_medium', params.medium);
  if (params.campaign) qs.set('utm_campaign', params.campaign);
  return {
    label: params.label ?? BRAND.primaryCtaLabel,
    href: `${BRAND.primaryCtaHref}?${qs.toString()}`,
  };
}

/** Canonical secondary CTA for pages that link to the in-page "how it works" anchor. */
export const SECONDARY_CTA_HOW_IT_WORKS = {
  label: 'See how it works',
  href: '#how-it-works',
} as const;

/** Canonical secondary CTA for pages that link to /pricing. */
export const SECONDARY_CTA_VIEW_PRICING = {
  label: 'View pricing',
  href: '/pricing',
} as const;

/** Canonical "ready to start" final-section heading + body — used by /case-studies, /tools, /integrations final CTAs. */
export const SHARED_FINAL_CTA_SECTION = {
  heading: 'Ready to start?',
  body: `Start AI Receptionist for $99/mo. Cancel anytime, with a 30-day money-back guarantee.`,
} as const;
